import { ApiClientError, isRecord, requestJson } from "./http.js";

export type TeamRole = "owner" | "admin" | "member" | "viewer";

export interface OrgSummary {
  readonly id: string;
  readonly name: string;
  readonly require2fa: boolean;
  readonly keyVersion: number;
  readonly role: TeamRole;
}

export interface OrgDetail extends OrgSummary {
  readonly recoveryPubB64: string | null;
  readonly memberCount: number;
  readonly rotationPending: boolean;
  readonly wrappedOrgKeyB64: string;
}

export interface OrgMemberView {
  readonly userId: string;
  readonly email: string | null;
  readonly role: TeamRole;
  readonly addedBy: string | null;
}

export interface TeamCollection {
  readonly id: string;
  readonly name: string;
  readonly createdAt: number;
}

export interface TeamItemMeta {
  readonly id: string;
  readonly collectionId: string;
  readonly folder: string;
  readonly titleHash: string;
  readonly version: number;
  readonly favorite: boolean;
  readonly updatedAt: number;
  readonly deletedAt: number | null;
}

export interface TeamItemFull extends TeamItemMeta {
  readonly ciphertextB64: string;
  readonly nonceB64: string;
}

export interface AuditEventView {
  readonly id: string;
  readonly action: string;
  readonly targetType: string;
  readonly targetId: string | null;
  readonly actorId: string | null;
  readonly createdAt: number;
}

export interface InviteView {
  readonly id: string;
  readonly email: string;
  readonly role: TeamRole;
  readonly expiresAt: number;
  readonly acceptedAt: number | null;
  /** The team key was rotated since: the invite can no longer be used. Older servers omit it. */
  readonly stale?: boolean | undefined;
}

export interface InviteLookup {
  readonly orgId: string;
  readonly orgName: string;
  readonly role: TeamRole;
  readonly email: string;
  readonly expiresAt: number;
  readonly wrappedOrgKeyB64: string;
  /** Older servers omit the fields below. */
  readonly inviteId?: string | undefined;
  readonly require2fa?: boolean | undefined;
  readonly twoFactorReady?: boolean | undefined;
  readonly claimed?: boolean | undefined;
  readonly stale?: boolean | undefined;
}

/** An open invite addressed to the signed-in account. Carries no key material. */
export interface PendingInvite {
  readonly id: string;
  readonly orgId: string;
  readonly orgName: string;
  readonly role: TeamRole;
  readonly expiresAt: number;
  readonly invitedByEmail: string | null;
  readonly require2fa: boolean;
  readonly twoFactorReady: boolean;
  /** The link was opened on some device, so the invite can be accepted from the list. */
  readonly claimed: boolean;
  /** The team key was rotated after the invite was made: it can only be removed. */
  readonly stale: boolean;
}

export interface OwnKey {
  readonly userId: string;
  readonly identityPubB64: string;
  readonly identityPrivEncB64: string;
}

export interface RotationMember {
  readonly userId: string;
  readonly wrappedOrgKeyB64: string;
}

export interface RotationItem {
  readonly collectionId: string;
  readonly itemId: string;
  readonly expectedVersion: number;
  readonly ciphertextB64: string;
  readonly nonceB64: string;
}

export interface TeamItemBody {
  readonly folder: string;
  readonly titleHash: string;
  readonly ciphertextB64: string;
  readonly nonceB64: string;
  readonly favorite: boolean;
}

export function isTeamRole(value: unknown): value is TeamRole {
  return value === "owner" || value === "admin" || value === "member" || value === "viewer";
}

export function isOrgSummary(value: unknown): value is OrgSummary {
  return isRecord(value) &&
    typeof value["id"] === "string" &&
    typeof value["name"] === "string" &&
    typeof value["require2fa"] === "boolean" &&
    typeof value["keyVersion"] === "number" &&
    isTeamRole(value["role"]);
}

export function isOrgDetail(value: unknown): value is OrgDetail {
  if (!isOrgSummary(value) || !isRecord(value)) {
    return false;
  }
  const recovery: unknown = value["recoveryPubB64"];
  const count: unknown = value["memberCount"];
  const pending: unknown = value["rotationPending"];
  const wrapped: unknown = value["wrappedOrgKeyB64"];
  return (recovery === null || typeof recovery === "string") &&
    typeof count === "number" &&
    typeof pending === "boolean" &&
    typeof wrapped === "string";
}

export function isOrgMemberView(value: unknown): value is OrgMemberView {
  return isRecord(value) &&
    typeof value["userId"] === "string" &&
    (value["email"] === null || typeof value["email"] === "string") &&
    isTeamRole(value["role"]) &&
    (value["addedBy"] === null || typeof value["addedBy"] === "string");
}

export function isTeamCollection(value: unknown): value is TeamCollection {
  return isRecord(value) &&
    typeof value["id"] === "string" &&
    typeof value["name"] === "string" &&
    typeof value["createdAt"] === "number";
}

export function isTeamItemMeta(value: unknown): value is TeamItemMeta {
  return isRecord(value) &&
    typeof value["id"] === "string" &&
    typeof value["collectionId"] === "string" &&
    typeof value["folder"] === "string" &&
    typeof value["titleHash"] === "string" &&
    typeof value["version"] === "number" &&
    typeof value["favorite"] === "boolean" &&
    typeof value["updatedAt"] === "number" &&
    (value["deletedAt"] === null || typeof value["deletedAt"] === "number");
}

export function isTeamItemFull(value: unknown): value is TeamItemFull {
  return isTeamItemMeta(value) &&
    isRecord(value) &&
    typeof value["ciphertextB64"] === "string" &&
    typeof value["nonceB64"] === "string";
}

export function isAuditEvent(value: unknown): value is AuditEventView {
  return isRecord(value) &&
    typeof value["id"] === "string" &&
    typeof value["action"] === "string" &&
    typeof value["targetType"] === "string" &&
    (value["targetId"] === null || typeof value["targetId"] === "string") &&
    (value["actorId"] === null || typeof value["actorId"] === "string") &&
    typeof value["createdAt"] === "number";
}

function isInviteView(value: unknown): value is InviteView {
  return isRecord(value) &&
    typeof value["id"] === "string" &&
    typeof value["email"] === "string" &&
    isTeamRole(value["role"]) &&
    typeof value["expiresAt"] === "number" &&
    (value["acceptedAt"] === null || typeof value["acceptedAt"] === "number") &&
    (value["stale"] === undefined || typeof value["stale"] === "boolean");
}

function isInviteLookup(value: unknown): value is InviteLookup {
  return isRecord(value) &&
    typeof value["orgId"] === "string" &&
    typeof value["orgName"] === "string" &&
    isTeamRole(value["role"]) &&
    typeof value["email"] === "string" &&
    typeof value["expiresAt"] === "number" &&
    typeof value["wrappedOrgKeyB64"] === "string" &&
    (value["inviteId"] === undefined || typeof value["inviteId"] === "string") &&
    (value["require2fa"] === undefined || typeof value["require2fa"] === "boolean") &&
    (value["twoFactorReady"] === undefined || typeof value["twoFactorReady"] === "boolean") &&
    (value["claimed"] === undefined || typeof value["claimed"] === "boolean") &&
    (value["stale"] === undefined || typeof value["stale"] === "boolean");
}

export function isPendingInvite(value: unknown): value is PendingInvite {
  return isRecord(value) &&
    typeof value["id"] === "string" &&
    typeof value["orgId"] === "string" &&
    typeof value["orgName"] === "string" &&
    isTeamRole(value["role"]) &&
    typeof value["expiresAt"] === "number" &&
    (value["invitedByEmail"] === null || typeof value["invitedByEmail"] === "string") &&
    typeof value["require2fa"] === "boolean" &&
    typeof value["twoFactorReady"] === "boolean" &&
    typeof value["claimed"] === "boolean" &&
    typeof value["stale"] === "boolean";
}

function unexpected(): Error {
  return new Error("Unerwartete Antwort vom Server.");
}

function arrayOf<T>(body: unknown, key: string, guard: (value: unknown) => value is T): T[] {
  if (!isRecord(body)) {
    throw unexpected();
  }
  const list: unknown = body[key];
  if (!Array.isArray(list)) {
    throw unexpected();
  }
  const out: T[] = [];
  for (const entry of list) {
    if (!guard(entry)) {
      throw unexpected();
    }
    out.push(entry);
  }
  return out;
}

function fieldOf<T>(body: unknown, key: string, guard: (value: unknown) => value is T): T {
  if (!isRecord(body)) {
    throw unexpected();
  }
  const value: unknown = body[key];
  if (!guard(value)) {
    throw unexpected();
  }
  return value;
}

function isCreatedOrg(value: unknown): value is { id: string; name: string; require2fa: boolean; keyVersion: number } {
  return isRecord(value) &&
    typeof value["id"] === "string" &&
    typeof value["name"] === "string" &&
    typeof value["require2fa"] === "boolean" &&
    typeof value["keyVersion"] === "number";
}

function isRotated(value: unknown): value is { id: string; keyVersion: number } {
  return isRecord(value) && typeof value["id"] === "string" && typeof value["keyVersion"] === "number";
}

function isUserKeyView(value: unknown): value is { userId: string; identityPubB64: string } {
  return isRecord(value) && typeof value["userId"] === "string" && typeof value["identityPubB64"] === "string";
}

function isOwnKey(value: unknown): value is OwnKey {
  return isRecord(value) &&
    typeof value["userId"] === "string" &&
    typeof value["identityPubB64"] === "string" &&
    typeof value["identityPrivEncB64"] === "string";
}

export async function listOrgs(): Promise<OrgSummary[]> {
  return arrayOf(await requestJson("/api/v1/orgs"), "orgs", isOrgSummary);
}

export async function fetchOrg(id: string): Promise<OrgDetail> {
  const body = await requestJson(`/api/v1/orgs/${id}`);
  if (!isOrgDetail(body)) {
    throw unexpected();
  }
  return body;
}

export async function createOrg(input: { id: string; name: string; wrappedOrgKeyB64: string }): Promise<{ id: string; name: string; require2fa: boolean; keyVersion: number }> {
  const body = await requestJson("/api/v1/orgs", { method: "POST", body: input });
  if (!isCreatedOrg(body)) {
    throw unexpected();
  }
  return body;
}

export async function patchOrg(id: string, patch: { name?: string; require2fa?: boolean }): Promise<void> {
  await requestJson(`/api/v1/orgs/${id}`, { method: "PATCH", body: patch });
}

export async function deleteOrg(id: string): Promise<void> {
  await requestJson(`/api/v1/orgs/${id}`, { method: "DELETE" });
}

export async function listOrgMembers(id: string): Promise<OrgMemberView[]> {
  return arrayOf(await requestJson(`/api/v1/orgs/${id}/members`), "members", isOrgMemberView);
}

export async function upsertMember(orgId: string, input: { userId: string; role: TeamRole; wrappedOrgKeyB64?: string | undefined }): Promise<void> {
  await requestJson(`/api/v1/orgs/${orgId}/members`, { method: "PUT", body: input });
}

export async function removeMember(orgId: string, userId: string): Promise<void> {
  await requestJson(`/api/v1/orgs/${orgId}/members/${userId}`, { method: "DELETE" });
}

export async function listCollections(orgId: string): Promise<TeamCollection[]> {
  return arrayOf(await requestJson(`/api/v1/orgs/${orgId}/collections`), "collections", isTeamCollection);
}

export async function createCollection(orgId: string, name: string): Promise<{ id: string; name: string }> {
  const body = await requestJson(`/api/v1/orgs/${orgId}/collections`, { method: "POST", body: { name } });
  if (!isRecord(body) || typeof body["id"] !== "string" || typeof body["name"] !== "string") {
    throw unexpected();
  }
  return { id: body["id"], name: body["name"] };
}

export async function renameCollection(id: string, name: string): Promise<void> {
  await requestJson(`/api/v1/collections/${id}`, { method: "PATCH", body: { name } });
}

export async function deleteCollection(id: string): Promise<void> {
  await requestJson(`/api/v1/collections/${id}`, { method: "DELETE" });
}

export async function listCollectionItems(collectionId: string): Promise<TeamItemFull[]> {
  return arrayOf(
    await requestJson(`/api/v1/collections/${collectionId}/items?includeDeleted=1`),
    "items",
    isTeamItemFull
  );
}

export async function createTeamItem(collectionId: string, id: string, body: TeamItemBody): Promise<TeamItemMeta> {
  const res = await requestJson(`/api/v1/collections/${collectionId}/items`, {
    method: "POST",
    body: { id, ...body }
  });
  return fieldOf(res, "item", isTeamItemMeta);
}

export async function patchTeamItem(
  collectionId: string,
  itemId: string,
  patch: Partial<TeamItemBody> & { expectedVersion: number }
): Promise<TeamItemFull> {
  const res = await requestJson(`/api/v1/collections/${collectionId}/items/${itemId}`, { method: "PATCH", body: patch });
  return fieldOf(res, "item", isTeamItemFull);
}

export async function deleteTeamItem(collectionId: string, itemId: string): Promise<TeamItemMeta> {
  const res = await requestJson(`/api/v1/collections/${collectionId}/items/${itemId}`, { method: "DELETE" });
  return fieldOf(res, "item", isTeamItemMeta);
}

export async function restoreTeamItem(collectionId: string, itemId: string): Promise<TeamItemMeta> {
  const res = await requestJson(`/api/v1/collections/${collectionId}/items/${itemId}/restore`, { method: "POST" });
  return fieldOf(res, "item", isTeamItemMeta);
}

export async function listRotationItems(orgId: string): Promise<TeamItemFull[]> {
  return arrayOf(await requestJson(`/api/v1/orgs/${orgId}/rotation-items`), "items", isTeamItemFull);
}

export async function rotateOrg(
  orgId: string,
  input: { wrappedKeys: ReadonlyArray<RotationMember>; items: ReadonlyArray<RotationItem> }
): Promise<{ id: string; keyVersion: number }> {
  const body = await requestJson(`/api/v1/orgs/${orgId}/rotate`, { method: "POST", body: input });
  if (!isRotated(body)) {
    throw unexpected();
  }
  return body;
}

export async function fetchOwnKey(): Promise<OwnKey | undefined> {
  try {
    const body = await requestJson("/api/v1/user-keys/me");
    if (!isOwnKey(body)) {
      throw unexpected();
    }
    return body;
  } catch (err: unknown) {
    if (err instanceof ApiClientError && err.status === 404) {
      return undefined;
    }
    throw err;
  }
}

export async function putOwnKey(identityPubB64: string, identityPrivEncB64: string): Promise<void> {
  await requestJson("/api/v1/user-keys", { method: "PUT", body: { identityPubB64, identityPrivEncB64 } });
}

export async function fetchUserPublicKey(userId: string): Promise<string> {
  const body = await requestJson(`/api/v1/user-keys/${userId}`);
  if (!isUserKeyView(body)) {
    throw unexpected();
  }
  return body.identityPubB64;
}

export async function listInvites(orgId: string): Promise<InviteView[]> {
  return arrayOf(await requestJson(`/api/v1/orgs/${orgId}/invites`), "invites", isInviteView);
}

export async function createInvite(
  orgId: string,
  input: { email: string; role: TeamRole; tokenB64: string; wrappedOrgKeyB64: string }
): Promise<{ id: string; email: string; role: TeamRole; expiresAt: number }> {
  const body = await requestJson(`/api/v1/orgs/${orgId}/invites`, { method: "POST", body: input });
  if (!isRecord(body) || typeof body["id"] !== "string" || typeof body["email"] !== "string" || !isTeamRole(body["role"]) || typeof body["expiresAt"] !== "number") {
    throw unexpected();
  }
  return { id: body["id"], email: body["email"], role: body["role"], expiresAt: body["expiresAt"] };
}

export async function revokeInvite(orgId: string, inviteId: string): Promise<void> {
  await requestJson(`/api/v1/orgs/${orgId}/invites/${inviteId}`, { method: "DELETE" });
}

export async function lookupInvite(token: string): Promise<InviteLookup> {
  const body = await requestJson("/api/v1/invites/lookup", { method: "POST", body: { token } });
  if (!isInviteLookup(body)) {
    throw unexpected();
  }
  return body;
}

export async function acceptInvite(token: string, wrappedOrgKeyB64: string): Promise<{ orgId: string; role: TeamRole }> {
  const body = await requestJson("/api/v1/invites/accept", { method: "POST", body: { token, wrappedOrgKeyB64 } });
  if (!isRecord(body) || typeof body["orgId"] !== "string" || !isTeamRole(body["role"])) {
    throw unexpected();
  }
  return { orgId: body["orgId"], role: body["role"] };
}

export async function listPendingInvites(): Promise<PendingInvite[]> {
  return arrayOf(await requestJson("/api/v1/invites/mine"), "invites", isPendingInvite);
}

export async function claimInvite(token: string, wrappedOrgKeyB64: string): Promise<void> {
  await requestJson("/api/v1/invites/claim", { method: "POST", body: { token, wrappedOrgKeyB64 } });
}

export async function acceptPendingInvite(inviteId: string): Promise<{ orgId: string; role: TeamRole }> {
  const body = await requestJson(`/api/v1/invites/${inviteId}/accept`, { method: "POST" });
  if (!isRecord(body) || typeof body["orgId"] !== "string" || !isTeamRole(body["role"])) {
    throw unexpected();
  }
  return { orgId: body["orgId"], role: body["role"] };
}

export async function declineInvite(inviteId: string): Promise<void> {
  await requestJson(`/api/v1/invites/${inviteId}/decline`, { method: "POST" });
}

export async function listAudit(orgId: string, limit: number = 50): Promise<AuditEventView[]> {
  return arrayOf(await requestJson(`/api/v1/orgs/${orgId}/audit?limit=${limit}`), "events", isAuditEvent);
}
